import { useCallback, useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";

/**
 * VovyNotch — the macOS-style notch UI from Figma (file oX227ByGQAwiIQLiZQ0h4C).
 *
 * It rides the top of the page on a loop: the idle shell sits for a beat, Vovy
 * speaks and the line types itself out, then it hands back to idle and lines up
 * the next one. The two faces flip over on the X axis as they swap.
 *
 * It's a real control underneath the loop, not a video — stop, mic, the send
 * arrow and the input all work. Touching any of them ends the loop and leaves
 * the notch wherever the person put it, since it's theirs at that point.
 */

type VoiceMode = "voice" | "ready";

/** The face the card opens on — and so the one an even turn count lands on. */
const INITIAL_MODE: VoiceMode = "ready";

/** Designed frame sizes, straight from the Figma states. The idle state's 24px
 *  top lip in Figma existed to meet the screen edge; floating, it becomes
 *  padding, so it matches the 10px below instead of leaving dead space. */
const FRAME: Record<VoiceMode, { width: number; height: number }> = {
  voice: { width: 440, height: 60 },
  ready: { width: 464, height: 50 },
};

/** Bar heights of the designed waveform, used as each bar's resting height. */
const WAVE_BARS = [8, 14, 20, 26, 16, 10, 22, 26, 12, 18, 24, 9];

/** What Vovy says, one after another, forever. */
const LINES = ["I’m ready to ship", "Your next feature is waiting", "Your idea is waiting"];

/** Per-character typing speed, how long a finished line sits up before it hands
 *  back to idle, and how long the idle shell holds before Vovy speaks again. */
const TYPE_MS = 55;
const LINE_HOLD = 3200;
const READY_HOLD = 2200;

export const NOTCH_FONT =
  "Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, system-ui, sans-serif";

/* ── Light theme ──────────────────────────────────────────────────────────
 * Exported rather than written twice: the Autopilot notch in VovyDoesSection
 * is the same object as this one and must not drift away from it.
 */

/** The shell — near-white over whatever it's floating on, a hairline edge to
 *  hold it off pale backgrounds, and a soft lift instead of a hard shadow.
 *
 *  Deliberately not frosted: `backdrop-filter` on this element sits directly
 *  over the flip card's 3D context, and that pairing is a known source of
 *  flicker in Safari. What it floats over is a smooth sky gradient or flat
 *  white, so a blur would have bought almost nothing to risk it for. */
export const NOTCH_SHELL =
  "bg-white/95 shadow-[0_10px_30px_-8px_rgba(2,32,58,0.30)]";

/** Type on that shell. The accent is the brand blue taken down until it clears
 *  4.5:1 on white — #3FAAF6 itself only manages 2.6:1, fine for a filled
 *  button or a waveform bar but not for anything you have to read. */
export const NOTCH_INK = "#1D1D1F";
export const NOTCH_INK_MUTED = "#6E6E73";
export const NOTCH_ACCENT = "#0B72B5";

/* ── Dusk theme (preview, ?theme=dusk on /) ───────────────────────────────
 * The same shell gone macOS-notch black. A flat colour rather than a
 * gradient so NotchFlare can match it exactly — a fillet even a shade off
 * the shell reads as a broken join. The light constants above stay as-is:
 * ExplainScreenPanel and TaskAutopilotPanel import them directly, and the
 * Autopilot notch in VovyDoesSection stays light in both themes.
 */
export type NotchTheme = "light" | "dusk";

export const DUSK_SHELL_COLOR = "rgba(10,11,15,0.95)";
export const DUSK_SHELL =
  "bg-[rgba(10,11,15,0.95)] ring-1 ring-inset ring-white/10 shadow-[0_12px_34px_-8px_rgba(0,0,0,0.65)]";
export const DUSK_INK = "#FFFFFF";
export const DUSK_INK_MUTED = "rgba(255,255,255,0.6)";

/** The card turn. A tween rather than a spring so the moment the card is
 *  edge-on is a known number — that's when the faces have to swap — and so it
 *  can't overshoot past the face it's landing on. */
const FLIP_MS = 560;
const FLIP = { duration: FLIP_MS / 1000, ease: [0.45, 0, 0.25, 1] as const };

/** When the turn has brought the far face round: half of it. */
const FLIP_HALF_MS = FLIP_MS / 2;

/**
 * One face of the card. Back faces are pre-turned 180° so they read right way
 * up once the card lands on them.
 *
 * `backface-visibility` alone is NOT enough to hide the face pointing away.
 * Firefox ignores it here — it flattens the 3D context under the shell's
 * `overflow: hidden` — and painted both faces stacked on each other, at rest,
 * so the notch read as the voice bar and the input bar mashed together. So the
 * inactive face is also stepped to opacity 0. Stepped, not faded: `duration: 0`
 * at the halfway mark means it cuts exactly when the card is edge-on, which is
 * what backface-visibility would have done and is invisible either way.
 *
 * (The two rotations compose to 360° on the back face, so even where the 3D
 * context does flatten, the face that lands is still the right way up.)
 *
 * Both faces stay mounted, so the one facing away is also taken out of the
 * document for pointers and screen readers. Its focusable bits carry
 * `tabIndex={-1}` at the call site for the same reason.
 */
const face = (active: boolean, back: boolean) => ({
  "aria-hidden": !active,
  initial: false,
  animate: { opacity: active ? 1 : 0 },
  transition: { duration: 0, delay: FLIP_HALF_MS / 1000 },
  style: {
    backfaceVisibility: "hidden" as const,
    WebkitBackfaceVisibility: "hidden" as const,
    transform: back ? "rotateX(180deg)" : undefined,
    pointerEvents: (active ? "auto" : "none") as "auto" | "none",
  },
});

/** Spring shared by the shell morph — quick, with just enough overshoot. */
const MORPH = { type: "spring" as const, stiffness: 420, damping: 34, mass: 0.9 };

/* ── Icons, taken from the Figma nodes ────────────────────────────────── */

export const VovyHeadIcon = ({
  className = "",
  mono = false,
}: {
  className?: string;
  /** Menu-bar style: single-colour mark in the current text colour. */
  mono?: boolean;
}) => (
  <svg
    viewBox="0 0 16 10.0839"
    fill="none"
    preserveAspectRatio="xMidYMid meet"
    aria-hidden="true"
    className={className}
  >
    <rect y="4.9571" width="1.05231" height="2.51582" fill={mono ? "currentColor" : "#3FAAF6"} />
    <rect
      x="14.8902"
      y="4.9571"
      width="1.05231"
      height="2.51582"
      fill={mono ? "currentColor" : "#3FAAF6"}
    />
    <path
      d="M8.4868 0.0373832C12.3323 0.037393 15.4496 3.15475 15.4496 7.00022C15.4496 8.68262 14.0858 10.0465 12.4034 10.0465H3.52088C1.8385 10.0465 0.474664 8.68262 0.474664 7.00022C0.474671 3.15475 3.59204 0.0373949 7.4375 0.0373832H8.4868ZM5.5265 2.51745C3.6552 2.51746 2.13823 4.03444 2.13821 5.90574C2.13821 7.77705 3.65519 9.29406 5.5265 9.29413H10.3459C12.2172 9.29413 13.7342 7.77705 13.7342 5.90574C13.7342 4.03443 12.2172 2.51745 10.3459 2.51745H5.5265ZM10.3467 3.1956C11.8706 3.19562 13.1059 4.43104 13.1059 5.95495C13.1059 7.47884 11.8706 8.71424 10.3467 8.71424H5.50409C3.98019 8.71424 2.74484 7.47884 2.74482 5.95495C2.74482 4.43104 3.98018 3.19561 5.50409 3.1956H10.3467ZM6.85302 7.49686C6.85302 7.90026 7.34224 8.22729 7.94572 8.22729C8.54931 8.22729 9.03858 7.90026 9.03858 7.49686H6.85302ZM5.48102 5.04191C5.25749 5.04191 5.0763 5.2231 5.0763 5.44662V6.4226C5.07631 6.64612 5.2575 6.82732 5.48102 6.82732C5.70453 6.82732 5.88572 6.64612 5.88573 6.4226V5.44662C5.88573 5.2231 5.70454 5.04191 5.48102 5.04191ZM10.4347 5.04191C10.2112 5.04191 10.03 5.2231 10.03 5.44662V6.4226C10.0301 6.64612 10.2112 6.82732 10.4347 6.82732C10.6582 6.82732 10.8394 6.64612 10.8395 6.4226V5.44662C10.8395 5.2231 10.6582 5.04191 10.4347 5.04191Z"
      fill={mono ? "currentColor" : "#3FAAF6"}
      stroke={mono ? "none" : "black"}
      strokeWidth="0.0747664"
    />
  </svg>
);

const MicIcon = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 14 25.0747"
    fill="none"
    preserveAspectRatio="none"
    aria-hidden="true"
    className={className}
  >
    <rect x="2.29868" y="0" width="9.19403" height="15.6716" rx="4.59702" fill="white" />
    <path
      d="M13.2632 11.9104C13.6701 11.9104 14.0049 12.2417 13.9518 12.6452C13.7684 14.0371 13.0676 15.3419 11.9497 16.343C10.637 17.5186 8.85652 18.1791 7 18.1791C5.14349 18.1791 3.36301 17.5186 2.05025 16.343C0.932371 15.3419 0.231634 14.0371 0.0482243 12.6452C-0.00493528 12.2417 0.329884 11.9104 0.736817 11.9104C1.14375 11.9104 1.46748 12.2425 1.53464 12.6439C1.70886 13.685 2.25091 14.6564 3.09227 15.4099C4.12866 16.338 5.53432 16.8594 7 16.8594C8.46568 16.8594 9.87134 16.338 10.9077 15.4099C11.7491 14.6564 12.2911 13.685 12.4654 12.6439C12.5325 12.2425 12.8563 11.9104 13.2632 11.9104Z"
      fill="white"
    />
    <line
      x1="7.10416"
      y1="24.03"
      x2="7.10416"
      y2="17.5523"
      stroke="white"
      strokeWidth="1.25373"
      strokeLinecap="round"
    />
    <line
      x1="3.9701"
      y1="24.4479"
      x2="10.4477"
      y2="24.4479"
      stroke="white"
      strokeWidth="1.25373"
      strokeLinecap="round"
    />
  </svg>
);

export const SparkleIcon = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 13.1961 13.1914" fill="none" aria-hidden="true" className={className}>
    <defs>
      <linearGradient id="vovy-notch-sparkle" x1="0" y1="6.6" x2="13.1" y2="6.6" gradientUnits="userSpaceOnUse">
        <stop stopColor="#3FAAF6" />
        <stop offset="1" stopColor="#5AC8FA" />
      </linearGradient>
    </defs>
    <path
      d="M6.59806 0.0956827C7.26973 3.54068 9.63139 5.90235 13.0981 6.59568C9.63139 7.28902 7.26973 9.65068 6.59806 13.0957C5.92639 9.65068 3.56472 7.28902 0.0980581 6.59568C3.56472 5.90235 5.92639 3.54068 6.59806 0.0956827Z"
      fill="url(#vovy-notch-sparkle)"
    />
  </svg>
);

/** How long one bar takes to dip and come back, and how far behind its
 *  neighbour each bar runs. Every bar shares the period and only the phase
 *  shifts, so the dip reads as a single wave travelling across rather than
 *  twelve bars each doing their own thing. */
const WAVE_PERIOD = 1.15;
const WAVE_STEP = 0.075;

/** The designed waveform, with a wave running through it. */
const Waveform = ({ animated = true }: { animated?: boolean }) => (
  <div className="flex h-[26px] w-[96px] shrink-0 items-center gap-[3px] overflow-hidden">
    {WAVE_BARS.map((h, i) => (
      <motion.div
        key={i}
        className="w-[4px] shrink-0 rounded-[2px] bg-[#3FAAF6]"
        style={{ height: h }}
        animate={
          animated
            ? { height: [h, Math.max(4, h * 0.3), h, Math.min(26, h * 1.15), h] }
            : undefined
        }
        transition={
          animated
            ? {
                duration: WAVE_PERIOD,
                times: [0, 0.25, 0.5, 0.75, 1],
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * WAVE_STEP,
              }
            : undefined
        }
      />
    ))}
  </div>
);

/* ── The voice notch ──────────────────────────────────────────────────── */

/** Concave corner fillets that join the notch to the top screen edge —
 *  the 180° flare of the real notch UI. A square outside each top corner,
 *  painted with a radial gradient whose inside-the-radius area is
 *  transparent, so what remains is the outward-sweeping curve. Color
 *  matches the active theme's shell exactly — bg-white/95 light,
 *  DUSK_SHELL_COLOR dusk. Hidden below `sm`, where the notch floats
 *  instead of hanging from the edge. */
export const FLARE = 18;
export const NotchFlare = ({
  side,
  theme = "light",
}: {
  side: "left" | "right";
  theme?: NotchTheme;
}) => (
  <span
    aria-hidden="true"
    className={`pointer-events-none absolute top-0 hidden sm:block ${
      side === "left" ? "left-[-18px]" : "right-[-18px]"
    }`}
    style={{
      width: FLARE,
      height: FLARE,
      background: `radial-gradient(circle at ${
        side === "left" ? "0" : "100%"
      } 100%, transparent ${FLARE - 0.5}px, ${
        theme === "dusk" ? DUSK_SHELL_COLOR : "rgba(255,255,255,0.95)"
      } ${FLARE}px)`,
    }}
  />
);

const VovyNotch = ({
  className = "",
  flared = false,
  theme = "light",
}: {
  className?: string;
  /** Hang from the top screen edge with flared corners (sm+); mobile keeps
   *  the floating pill. */
  flared?: boolean;
  /** "dusk" turns the shell notch-black with white ink; the brand-blue
   *  accents stay — they pop on black. */
  theme?: NotchTheme;
}) => {
  // Opens on the idle shell, then the loop takes it into the voice state.
  const [mode, setMode] = useState<VoiceMode>(INITIAL_MODE);
  const [prompt, setPrompt] = useState("");
  const [transcript, setTranscript] = useState("");
  const [lineIndex, setLineIndex] = useState(0);
  // Off for good the moment someone drives the notch themselves.
  const [auto, setAuto] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  // Half-turns the card has taken. Counting them instead of animating between
  // two angles keeps it turning the same way every time rather than winding
  // back the way it came. Parity is the invariant the faces rely on: the card
  // opens on "ready" at 0, and mode only ever toggles, so an even count is
  // always the ready face and an odd one always the voice face.
  const [turns, setTurns] = useState(0);
  const [turnedAt, setTurnedAt] = useState<VoiceMode>(INITIAL_MODE);
  if (turnedAt !== mode) {
    setTurnedAt(mode);
    setTurns((t) => t + 1);
  }

  const prefersReducedMotion = useReducedMotion();

  // Nothing animates in a hidden tab, so a loop left running there would stack
  // up transitions and snap through them on return. Hold it instead.
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const onChange = () => setVisible(!document.hidden);
    onChange();
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  const talking = mode === "voice" && visible && !prefersReducedMotion;
  const line = LINES[lineIndex % LINES.length];
  const frame = FRAME[mode];
  const dusk = theme === "dusk";
  const ink = dusk ? DUSK_INK : NOTCH_INK;
  /** Which face the card is landing on — see the parity note above. */
  const showVoice = turns % 2 === 1;

  /** Any hand on the controls stops the loop for good. */
  const takeOver = useCallback(() => setAuto(false), []);

  // Read inside a callback that must not re-create itself on every mode change.
  const modeRef = useRef(mode);
  modeRef.current = mode;

  /** Hands over to the idle field — the only place there is to type. From the
   *  voice face that means turning the card first and focusing once the field
   *  has come round. preventScroll because the notch is fixed to the top of
   *  the page: focusing must never yank the page under it. */
  const askByHand = useCallback(() => {
    setAuto(false);
    if (modeRef.current === "ready") {
      inputRef.current?.focus({ preventScroll: true });
      return;
    }
    setMode("ready");
    window.setTimeout(() => inputRef.current?.focus({ preventScroll: true }), FLIP_HALF_MS);
  }, []);

  const backToVoice = useCallback(() => {
    setAuto(false);
    setTranscript("");
    setMode("voice");
  }, []);

  // The current line types itself out, one character at a time. Clears first so
  // a re-entry never flashes the last line before retyping.
  useEffect(() => {
    if (!talking) return;
    setTranscript("");
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setTranscript(line.slice(0, i));
      if (i >= line.length) window.clearInterval(id);
    }, TYPE_MS);
    return () => window.clearInterval(id);
  }, [talking, line]);

  // Once the line has landed, hold a beat, queue the next one, and hand back to
  // the idle shell. Driving it by hand keeps the lines cycling in place instead
  // — the person chose the voice state, so it stays there.
  useEffect(() => {
    if (!talking || transcript !== line) return;
    const id = window.setTimeout(() => {
      setLineIndex((n) => n + 1);
      if (auto) setMode("ready");
    }, LINE_HOLD);
    return () => window.clearTimeout(id);
  }, [auto, talking, transcript, line]);

  // The idle shell holds its beat, then Vovy starts listening again.
  useEffect(() => {
    if (!auto || mode !== "ready" || !visible || prefersReducedMotion) return;
    const id = window.setTimeout(() => setMode("voice"), READY_HOLD);
    return () => window.clearTimeout(id);
  }, [auto, mode, visible, prefersReducedMotion]);

  const shell = (
    <motion.div
      animate={{ width: frame.width, height: frame.height }}
      initial={false}
      transition={MORPH}
      style={{ fontFamily: NOTCH_FONT, perspective: 900 }}
      className={`relative overflow-hidden rounded-[18px] ${
        flared ? "sm:rounded-t-none" : ""
      } ${dusk ? DUSK_SHELL : NOTCH_SHELL} ${className}`}
    >
      {/* The card. Both faces stay mounted and back-to-back on it, so one
          continuous turn takes the old one away and brings the new one round —
          rather than two half-turns handing off in the middle, which is what
          gives a crossfade away as a crossfade. Nothing fades: a face is simply
          gone once it passes edge-on, the way a real card is. */}
      <motion.div
        animate={{ rotateX: turns * 180 }}
        initial={false}
        transition={FLIP}
        style={{ transformStyle: "preserve-3d" }}
        className="relative h-full w-full"
      >
        {/* ── Listening (live mic) ── */}
        <motion.div
          {...face(showVoice, true)}
          className="absolute inset-0 flex items-center gap-[10px] px-[14px] py-[6px]"
        >
          <SparkleIcon className="size-[13px] shrink-0" />
          <Waveform animated={talking} />

          {/* leading-[1.4], not leading-none: `truncate` brings overflow-hidden
              with it, and a line box the exact size of the font clips every
              descender — the g in "waiting" loses its tail */}
          <p
            className="min-w-px flex-1 truncate text-[12.5px] font-medium leading-[1.4]"
            style={{ color: ink }}
          >
            {transcript || "Listening..."}
          </p>

          {/* hands off to the idle field, which is the only place there is to
              type — the input lives on the other face */}
          <button
            type="button"
            tabIndex={showVoice ? 0 : -1}
            onClick={askByHand}
            aria-label="Type instead"
            className="flex size-[26px] shrink-0 items-center justify-center overflow-hidden rounded-[13px] bg-[#3FAAF6] transition-colors hover:bg-[#5AC8FA]"
          >
            <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="size-[12px]">
              <path
                d="M6 10V2M6 2L2.5 5.5M6 2L9.5 5.5"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            type="button"
            tabIndex={showVoice ? 0 : -1}
            onClick={() => {
              takeOver();
              setMode("ready");
            }}
            aria-label="Stop listening"
            className="relative size-[26px] shrink-0 overflow-hidden rounded-[13px] border-[1px] border-solid border-[rgba(255,59,48,0.32)] bg-[rgba(255,59,48,0.10)] transition-colors hover:bg-[rgba(255,59,48,0.20)]"
          >
            <span className="absolute left-[8px] top-[8px] size-[8px] rounded-[2px] bg-[#FF3B30]" />
          </button>
        </motion.div>

        {/* ── Ready (idle/default) — the face with the real input ── */}
        <motion.div
          {...face(!showVoice, false)}
          className="absolute inset-0 flex flex-col items-start justify-center py-[10px]"
        >
          <div className="flex h-[30px] w-full shrink-0 items-center gap-[8px] px-[12px]">
            <button
              type="button"
              tabIndex={showVoice ? -1 : 0}
              onClick={askByHand}
              aria-label="Ask Vovy"
              className={`relative size-[26px] shrink-0 overflow-hidden rounded-[13px] border-[1px] border-solid transition-colors ${
                dusk
                  ? "border-white/10 bg-[rgba(63,170,246,0.22)] hover:bg-[rgba(63,170,246,0.34)]"
                  : "border-[rgba(2,32,58,0.08)] bg-[rgba(63,170,246,0.14)] hover:bg-[rgba(63,170,246,0.26)]"
              }`}
            >
              <VovyHeadIcon className="absolute left-[4.5px] top-[7.3px] h-[9.39px] w-[15px]" />
            </button>

            <div
              className={`flex h-[24px] min-w-px flex-1 items-center overflow-hidden rounded-[12px] border-[1px] border-solid px-[12px] transition-colors focus-within:border-[rgba(63,170,246,0.6)] ${
                dusk
                  ? "border-white/15 bg-white/[0.06]"
                  : "border-[rgba(2,32,58,0.12)] bg-[rgba(2,32,58,0.04)]"
              }`}
            >
              <input
                ref={inputRef}
                value={prompt}
                tabIndex={showVoice ? -1 : 0}
                onFocus={takeOver}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && prompt.trim()) {
                    setPrompt("");
                    backToVoice();
                  }
                }}
                placeholder="Ask Vovy..."
                aria-label="Ask Vovy"
                style={{ color: ink }}
                className={`w-full bg-transparent font-[inherit] text-[13px] leading-[1.4] outline-none ${
                  dusk ? "placeholder:text-white/50" : "placeholder:text-[#6E6E73]"
                }`}
              />
            </div>

            <button
              type="button"
              tabIndex={showVoice ? -1 : 0}
              onClick={backToVoice}
              aria-label="Start voice input"
              className="relative size-[26px] shrink-0 overflow-hidden rounded-[13px] bg-[#3FAAF6] transition-colors hover:bg-[#5AC8FA]"
            >
              <MicIcon className="absolute left-[10.2px] top-[7.46px] h-[10.29px] w-[5.74px]" />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );

  if (!flared) return shell;
  return (
    <div className="relative">
      <NotchFlare side="left" theme={theme} />
      <NotchFlare side="right" theme={theme} />
      {shell}
    </div>
  );
};

export default VovyNotch;
